import React, { useEffect, useState } from 'react';
import api from '../services/api';
import type StudyModel from '../models/StudyModel';
import type UserModel from '../models/UserModel';
import LoadingSpinner from './LoadingSpinner';
import StudySearchField from './StudySearchField';
import UserSearchField from './UserSearchField';

type PropTypes = {
    onParticipantCreated: () => void;
    study?: StudyModel;
};

function CreateStudyParticipantForm({
    onParticipantCreated,
    study,
}: PropTypes) {
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedStudy, setSelectedStudy] = useState(
        study ?? ({} as StudyModel), // eslint-disable-line @typescript-eslint/consistent-type-assertions
    );
    const [selectedUser, setSelectedUser] = useState({} as UserModel);
    const [startDate, setStartDate] = useState('');
    const [participantId, setParticipantId] = useState('');

    useEffect(() => {
        if (
            selectedStudy &&
            new Date(startDate) < new Date(selectedStudy.start_date)
        ) {
            setError(
                `Start date must be after study start date: ${selectedStudy.start_date}`,
            );
            return;
        }

        setError('');
    }, [startDate, selectedStudy]);

    useEffect(() => {
        setError('');
    }, [selectedUser]);

    const handleSubmit = async () => {
        if (!selectedStudy || !selectedUser || !startDate) {
            setError('All fields are required');
            return;
        }

        setLoading(true);
        const { success } = await api.createStudyParticipant(
            Number(selectedStudy.id),
            Number(selectedUser.id),
            startDate,
            participantId,
        );
        setLoading(false);
        if (success) {
            onParticipantCreated();
        } else {
            setError('Failed to create participant. Please try again later.');
        }
    };

    return (
        <div className="w-[300px]">
            {!study && (
                <div className="mt-2 flex items-center justify-between">
                    <label>Study:</label>
                    <StudySearchField
                        selectedStudy={selectedStudy}
                        setSelectedStudy={setSelectedStudy}
                    />
                </div>
            )}
            <div className="mt-2 flex items-center justify-between">
                <label>Participant:</label>
                <UserSearchField
                    selectedUser={selectedUser}
                    setSelectedUser={setSelectedUser}
                />
            </div>
            <div className="mt-2 flex items-center justify-between">
                <label>Participant ID:</label>
                <input
                    className="border border-black rounded text-primary p-1 w-1/2"
                    type="text"
                    name="participant"
                    value={participantId}
                    onChange={(event) => {
                        setParticipantId(event.target.value);
                    }}
                />
            </div>
            <div className="mt-2 flex items-center justify-between">
                <label>Start Date:</label>
                <input
                    className="border border-black rounded text-primary p-1 w-1/2"
                    type="date"
                    name="startDate"
                    value={startDate}
                    onChange={(event) => {
                        setStartDate(event.target.value);
                    }}
                />
            </div>
            {error && <div className="text-red-500 mt-2">{error}</div>}
            <button
                type="submit"
                className="btn btn-primary mt-6"
                disabled={loading || error !== ''}
                onClick={handleSubmit}
            >
                {loading ? <LoadingSpinner size={30} /> : 'Add Participant'}
            </button>
        </div>
    );
}

export default CreateStudyParticipantForm;
